import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '../../../../../../components/ui/dialog';
import { Button } from '../../../../../../components/ui/button';
import { FileUpload } from '../../../../../../components/ui/FileUpload';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../../components/ui/select';
import { Label } from '../../../../../../components/ui/label';
import { Upload, FileText, X, CheckCircle, AlertTriangle, Cpu } from 'lucide-react';
import weldAxiosConfig from '../../../../../../redux/WeldData/axiosConfig';
import customNotification from '../../../../../../components/ui/custom-notification';
import { logWeldLogUploaded } from '../../../../utils/productionAudit';
import { useProductionModalAudit } from '../../../../../audit/useProductionModalAudit';
import { ManualWeldFolderUploadPanel } from './ManualWeldFolderUploadPanel';
import {
  WeldingSystemConfigEnum,
  MANUAL_MACHINE_SELECT_VALUE,
  isWeldingSystemConfig,
  type WeldDataMachineSelectValue,
} from './weldingSystemConfig';

/** Re-export for existing imports. */
export { WeldingSystemConfigEnum, MANUAL_MACHINE_SELECT_VALUE } from './weldingSystemConfig';

interface MachineTypeOption {
  value: WeldDataMachineSelectValue;
  label: string;
  /** Short hint shown under the dropdown label. */
  description?: string;
}

const MACHINE_TYPE_OPTIONS: MachineTypeOption[] = [
  { value: MANUAL_MACHINE_SELECT_VALUE, label: 'Manual' },
  { value: WeldingSystemConfigEnum.P600Z, label: 'P600Z', description: 'Pipeliner 600Z' },
  { value: WeldingSystemConfigEnum.M500, label: 'M500', description: 'Manual / M500 series' },
  { value: WeldingSystemConfigEnum.P625, label: 'P625', description: 'Pipeliner 625' },
  { value: WeldingSystemConfigEnum.IWM, label: 'IWM', description: 'Internal welding machine' },
];

const isMachineSelectValue = (v: string): v is WeldDataMachineSelectValue =>
  v === MANUAL_MACHINE_SELECT_VALUE || isWeldingSystemConfig(v);

interface UploadWeldDataDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete?: () => void;
  project?: any;
}

interface TxtParseResult {
  payload: WeldUploadPayload | null;
  previewRows: ParsedTxtRow[];
  error: string | null;
}

interface ParsedTxtRow {
  stationNumber: string;
  eventNo: number;
  jobNumber: string;
  weldNumber: string;
  passName: string;
  torch: string;
  travelSpeed: number;
  volts: number;
  current: number;
  wireSpeed: number;
  time: number;
}

interface WeldUploadPayload {
  project_id: number;
  machine_type: string;
  records: Array<Record<string, any>>;
}
const WELD_BULK_UPLOAD_ENDPOINT =
  process.env.REACT_APP_WELD_BULK_UPLOAD_ENDPOINT || '/log/bulkCreateUpdateLogs';



const normalizeHeader = (value: string): string =>
  String(value || '')
    .replace(/^\uFEFF/, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .trim();

const parseNumber = (value: string | undefined, defaultValue = 0): number => {
  if (value == null) return defaultValue;
  const n = Number(String(value).trim());
  return Number.isFinite(n) ? n : defaultValue;
};

const parseString = (value: string | undefined, defaultValue = ''): string =>
  String(value ?? defaultValue).trim();

const getFirstValue = (row: Record<string, string>, aliases: string[]): string => {
  for (const key of aliases) {
    const value = row[key];
    if (value != null && String(value).trim() !== '') return String(value);
  }
  return '';
};

/** Instrument log times are fractional epoch-like seconds (e.g. "1777664.786"); compare numerically for max end time. */
const parseInstrumentTimeSeconds = (value: string): number => {
  const n = parseFloat(String(value || '').trim());
  return Number.isFinite(n) ? n : NaN;
};

const buildUnixTimestamp = (row: Record<string, string>, fallbackSeconds = 0): number => {
  const year = parseNumber(row.year, 0);
  const month = parseNumber(row.month, 0);
  const day = parseNumber(row.day, 0);
  const hour = parseNumber(row.hour, 0);
  const minute = parseNumber(row.minute, 0);
  const second = parseNumber(row.second, 0);

  if (year > 0 && month > 0 && day > 0) {
    const utcMs = Date.UTC(year, month - 1, day, hour, minute, second);
    if (Number.isFinite(utcMs)) return Math.floor(utcMs / 1000);
  }

  return fallbackSeconds;
};

type RowMap = Record<string, string>;

interface ParsedLine {
  row: RowMap;
  lineIndex: number;
}

/**
 * Headers required in the TXT file for parsing to succeed.
 * `label` is what we show to the user (matches the printed column heading);
 * `aliases` lists the post-`normalizeHeader` slugs the parser will accept for that column.
 */
const REQUIRED_HEADER_GROUPS: { label: string; aliases: string[] }[] = [
  { label: 'Evt #', aliases: ['evt', 'evt_no', 'evt_number'] },
  { label: 'Time', aliases: ['time'] },
  { label: 'Start Time', aliases: ['start_time', 's_time'] },
  { label: 'Job #', aliases: ['job', 'job_no', 'job_number'] },
  { label: 'Weld #', aliases: ['weld', 'weld_no', 'weld_number'] },
  { label: 'Pass', aliases: ['ld_pass', 'lead_pass', 'pass', 'pass_name', 'ld_pass_name'] },
  { label: 'Travel speed (TVL_IPM)', aliases: ['tvl_ipm'] },
  { label: 'Lead volts (LD_Volts)', aliases: ['ld_volts'] },
  { label: 'Lead amps (LD_Amps)', aliases: ['ld_amps'] },
  { label: 'Year', aliases: ['year'] },
];

const parseTxtToPayload = (
  fileText: string,
  projectId: number,
  machineType: WeldingSystemConfigEnum,
): TxtParseResult => {
  const lines = fileText
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter((line) => line.trim().length > 0);

  if (lines.length < 2) {
    return {
      payload: null,
      previewRows: [],
      error: 'File is empty or does not contain data rows. Expected a tab-separated header row followed by weld records.',
    };
  }

  // First line must be a tab-separated header row (the welding-machine TXT export format).
  if (!lines[0].includes('\t')) {
    return {
      payload: null,
      previewRows: [],
      error:
        'This does not look like a welding-machine TXT export — the first line is not tab-separated. ' +
        'Please upload the original TXT file produced by the machine.',
    };
  }

  const headerColumnsRaw = lines[0].split('\t').map((h) => normalizeHeader(h));
  const duplicateCounter: Record<string, number> = {};
  const headerColumns = headerColumnsRaw.map((base) => {
    const key = base || 'col';
    duplicateCounter[key] = (duplicateCounter[key] || 0) + 1;
    return duplicateCounter[key] === 1 ? key : `${key}_${duplicateCounter[key]}`;
  });
  const headerSet = new Set(headerColumns);
  const allHeadersBlank = headerColumns.every((h) => !h || h === 'col');
  if (allHeadersBlank) {
    return {
      payload: null,
      previewRows: [],
      error: 'No column headers were detected in the first line of the file. Please upload the original welding-machine TXT export.',
    };
  }

  const missingHeaderLabels = REQUIRED_HEADER_GROUPS
    .filter((group) => !group.aliases.some((alias) => headerSet.has(alias)))
    .map((group) => group.label);

  if (missingHeaderLabels.length > 0) {
    return {
      payload: null,
      previewRows: [],
      error:
        `The selected file is missing required column${missingHeaderLabels.length > 1 ? 's' : ''}: ` +
        `${missingHeaderLabels.join(', ')}. ` +
        'Please make sure you upload the original welding-machine TXT export with all standard headers.',
    };
  }

  const parsedLines: ParsedLine[] = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cols = lines[i].split('\t');
    if (!cols.length || cols.every((c) => c.trim() === '')) continue;
    const row: RowMap = {};
    headerColumns.forEach((key, idx) => {
      row[key] = cols[idx] ?? '';
    });
    parsedLines.push({ row, lineIndex: i });
  }

  const segments: ParsedLine[][] = [];
  let segmentBuffer: ParsedLine[] = [];
  let prevStartKey = '__init__';

  for (let i = 0; i < parsedLines.length; i += 1) {
    const pl = parsedLines[i];
    const startKeyRaw = parseString(getFirstValue(pl.row, ['start_time']));
    const key = startKeyRaw || `${pl.lineIndex}_${getFirstValue(pl.row, ['time'])}_${getFirstValue(pl.row, ['evt'])}`;

    if (segmentBuffer.length === 0 || key === prevStartKey) {
      segmentBuffer.push(pl);
      prevStartKey = key;
      continue;
    }

    segments.push(segmentBuffer);
    segmentBuffer = [pl];
    prevStartKey = key;
  }
  if (segmentBuffer.length) segments.push(segmentBuffer);

  const records: Array<Record<string, any>> = [];
  const previewRows: ParsedTxtRow[] = [];

  for (const segment of segments) {
    const first = segment[0];
    const last = segment[segment.length - 1];
    const row0 = first.row;
    const sampleTimeFirst = parseString(getFirstValue(row0, ['time']));
    /** TXT "Start Time" column as string (e.g. 1777664.764) for S_Time on T/C */
    const startTimeInstrumentStr = parseString(getFirstValue(row0, ['start_time']));
    /** Must match T/C S_Time so welduid_s_event_torch equals S record (avoids duplicate pass_view). */
    const instrumentStartStr = startTimeInstrumentStr || sampleTimeFirst;

    const firstEventNoText =
      parseString(getFirstValue(row0, ['evt', 'evt_no', 'evt_number'])) ||
      String(parseNumber(getFirstValue(row0, ['evt', 'evt_no', 'evt_number']), 0));

    const jobNumber = parseString(getFirstValue(row0, ['job', 'job_no', 'job_number']));
    const weldNumber = parseString(getFirstValue(row0, ['weld', 'weld_no', 'weld_number']));

    const torch = 'Lead';

    /** Job#_Weld# e.g. N3513 14-CMT-DT_001 */
    const weldUid =
      jobNumber && weldNumber
        ? `${jobNumber}_${weldNumber}`
        : `${jobNumber || 'JOB'}_${weldNumber || 'WELD'}`;

    /** First "Bug #" column = bug type id; second "Bug" = work side label */
    const bugNumber0 = parseString(getFirstValue(row0, ['bug', 'bug_1', 'bug_number']));
    const bugType0 = parseString(getFirstValue(row0, ['bug_2', 'bug_type', 'bug']));

    /** One S row per segment — shape matches device-log upload API. */
    const sRecord = {
      Record: 'S',
      Event: firstEventNoText,
      Time: instrumentStartStr,
      S_Time: instrumentStartStr,
      Edge_UID: '',
      Weld_UID: weldUid,
      Weld_Type: machineType,
      Bug_number: bugNumber0,
      Station_number: parseString(getFirstValue(row0, ['stn', 'stn_no', 'station_number'])),
      Bug_type: bugType0,
      Pipe_diameter: parseString(getFirstValue(row0, ['p_dia_in'])),
      Band_diameter: parseString(getFirstValue(row0, ['b_dia_in'])),
      Logging_distance: parseString(getFirstValue(row0, ['logdist_mil'])),
      Job_number: jobNumber,
      Weld_number: weldNumber,
      Lead_wire_diameter: parseString(getFirstValue(row0, ['ld_wiredia_mil', 'ld_wire_dia_mil'])),
      Trail_wire_diameter: parseString(getFirstValue(row0, ['tr_wiredia_mil', 'tr_wire_dia_mil'])),
      Year: parseString(getFirstValue(row0, ['year'])),
      Month: parseString(getFirstValue(row0, ['month'])),
      Day: parseString(getFirstValue(row0, ['day'])),
      Hour: parseString(getFirstValue(row0, ['hour'])),
      Minute: parseString(getFirstValue(row0, ['minute'])),
      Second: parseString(getFirstValue(row0, ['second'])),
    };

    records.push(sRecord);

    /** Greatest numeric `time` in segment — C record should close at true end sample, not last file row order. */
    let maxInstrumentTime = -Infinity;
    let maxInstrumentTimeStr = '';
    /** Last non-empty `time` string seen — fallback when values do not parse as numbers. */
    let lastSampleTimeStr = '';

    /** T rows: one per TXT row in this segment only (between single S and single C). */
    for (let si = 0; si < segment.length; si += 1) {
      const { row } = segment[si];

      const eventNo = parseNumber(getFirstValue(row, ['evt', 'evt_no', 'evt_number']), 0);
      const eventNoText =
        eventNo ? String(eventNo) : parseString(getFirstValue(row, ['evt', 'evt_no', 'evt_number']));
      const jobNum = parseString(getFirstValue(row, ['job', 'job_no', 'job_number']));
      const weldNum = parseString(getFirstValue(row, ['weld', 'weld_no', 'weld_number']));
      const passName = parseString(
        getFirstValue(row, [
          'ld_pass',
          'ld_pass_2',
          'lead_pass',
          'pass',
          'pass_name',
          'ld_pass_name',
          'lead_pass_name',
        ]),
        'UNKNOWN'
      );
      const trailPassName = parseString(
        getFirstValue(row, [
          'tr_pass',
          'tr_pass_2',
          'trail_pass',
          'trail_pass_name',
          'tr_pass_name',
        ])
      );
      const eventTime = parseString(getFirstValue(row, ['time']));
      if (eventTime) {
        lastSampleTimeStr = eventTime;
        const tn = parseInstrumentTimeSeconds(eventTime);
        if (Number.isFinite(tn) && tn >= maxInstrumentTime) {
          maxInstrumentTime = tn;
          maxInstrumentTimeStr = eventTime;
        }
      }

      const time = buildUnixTimestamp(row, eventNo);

      const travelSpeed = parseNumber(row.tvl_ipm, 0);
      const volts = parseNumber(row.ld_volts, 0);
      const current = parseNumber(row.ld_amps, 0);
      const wireSpeed = parseNumber(row.ld_wire_ipm, 0);

      const tRecord = {
        Record: 'T',
        Event: eventNoText,
        Time: eventTime,
        S_Event: firstEventNoText,
        S_Time: instrumentStartStr,
        Edge_UID: '',
        Weld_UID: weldUid,
        Station_number: parseString(getFirstValue(row0, ['stn', 'stn_no', 'station_number'])),
        Weld_Type: machineType,
        Job_number: jobNum || jobNumber,
        jobNumber: jobNum || jobNumber,
        Tilt: parseString(getFirstValue(row, ['tilt'])),
        Travel_speed: parseString(getFirstValue(row, ['tvl_ipm'])),
        Distance: parseString(getFirstValue(row, ['dist'])),
        Lead_pass_name: passName,
        Lead_volts: parseString(getFirstValue(row, ['ld_volts', 'lead_volts'])),
        Lead_amps: parseString(getFirstValue(row, ['ld_amps', 'lead_amps'])),
        Lead_wire_speed: parseString(getFirstValue(row, ['ld_wire_ipm', 'lead_wire_ipm', 'ld_wire_speed'])),
        Lead_oscillate_width: parseString(getFirstValue(row, ['ld_width_mil', 'lead_width_mil', 'ld_oscillate_width'])),
        Lead_target: parseString(getFirstValue(row, ['ld_tgt_vlt', 'lead_tgt_vlt', 'ld_target'])),
        Lead_horizontal_bias: parseString(getFirstValue(row, ['ld_cs_bias', 'lead_cs_bias'])),
        Lead_frequency: parseString(getFirstValue(row, ['ld_bpm', 'lead_bpm'])),
        Lead_total_wire_consumed: parseString(
          getFirstValue(row, ['ld_tot_wire_in', 'lead_tot_wire_in', 'ld_total_wire_consumed'])
        ),
        Lead_heat: parseString(getFirstValue(row, ['ld_true_energy', 'lead_true_energy', 'ld_heat'])),
        Trail_pass_name: trailPassName || passName,
        Trail_volts: parseString(getFirstValue(row, ['tr_volts', 'trail_volts', 'tr_vlt'])),
        Trail_amps: parseString(getFirstValue(row, ['tr_amps', 'trail_amps', 'tr_amp'])),
        Trail_wire_speed: parseString(
          getFirstValue(row, ['tr_wire_ipm', 'trail_wire_ipm', 'tr_wire_speed', 'trail_wire_speed'])
        ),
        Trail_oscillate_width: parseString(
          getFirstValue(row, ['tr_width_mil', 'trail_width_mil', 'tr_oscillate_width'])
        ),
        Trail_target: parseString(getFirstValue(row, ['tr_tgt_vlt', 'trail_tgt_vlt', 'tr_target'])),
        Trail_horizontal_bias: parseString(getFirstValue(row, ['tr_cs_bias', 'trail_cs_bias'])),
        Trail_frequency: parseString(getFirstValue(row, ['tr_bpm', 'trail_bpm'])),
        Trail_total_wire_consumed: parseString(
          getFirstValue(row, ['tr_tot_wire_in', 'trail_tot_wire_in', 'tr_total_wire_consumed'])
        ),
        Trail_heat: parseString(getFirstValue(row, ['tr_true_energy', 'trail_true_energy', 'tr_heat'])),
      };

      records.push(tRecord);

      if (previewRows.length < 5) {
        previewRows.push({
          stationNumber: parseString(getFirstValue(row0, ['stn', 'stn_no', 'station_number'])),
          eventNo,
          jobNumber: jobNum || jobNumber,
          weldNumber: weldNum || weldNumber,
          passName,
          torch,
          travelSpeed,
          volts,
          current,
          wireSpeed,
          time,
        });
      }
    }

    /** One C row per segment — Time is max sample `time` in segment (welding end), not necessarily last TXT row. */
    const rowN = last.row;
    let cTimeStr = maxInstrumentTimeStr;
    if (!cTimeStr) {
      cTimeStr = parseString(getFirstValue(rowN, ['time']));
    }
    if (!cTimeStr) {
      cTimeStr = lastSampleTimeStr;
    }
    if (!cTimeStr) {
      const lastEvt = parseNumber(getFirstValue(rowN, ['evt', 'evt_no', 'evt_number']), 0);
      const ts = buildUnixTimestamp(rowN, lastEvt);
      if (ts > 0) cTimeStr = String(ts);
    }

    const cRecord = {
      Record: 'C',
      Event: firstEventNoText,
      Time: cTimeStr,
      S_Event: firstEventNoText,
      S_Time: instrumentStartStr,
      Edge_UID: '',
      Weld_UID: weldUid,
      Weld_Type: machineType,
      Gps: {
        Latitude: '',
        Longitude: '',
        Confidence: '',
        HDOP: '',
      },
    };

    records.push(cRecord);
  }

  if (records.length === 0) {
    return {
      payload: null,
      previewRows: [],
      error: 'No valid data rows found in the uploaded TXT file.',
    };
  }
  //  const testrecords = records.slice(0, 28); //28,131
  //  console.log("testrecords", testrecords);

  console.log("Total records", records.length);
  console.log("Total T records", records.filter(record => record.Record === 'T').length);
  console.log("Total S records", records.filter(record => record.Record === 'S').length);
  console.log("Total C records", records.filter(record => record.Record === 'C').length);


  return {
    payload: {
      project_id: projectId,
      machine_type: machineType,
      records: records,
    },
    previewRows,
    error: null,
  };
};

export const UploadWeldDataDialog: React.FC<UploadWeldDataDialogProps> = ({
  isOpen,
  onClose,
  onUploadComplete,
  project,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [previewRows, setPreviewRows] = useState<ParsedTxtRow[]>([]);
  const [payload, setPayload] = useState<WeldUploadPayload | null>(null);
  /** Empty string means "not yet picked" — user must select before uploading. */
  const [machineType, setMachineType] = useState<WeldDataMachineSelectValue | ''>('');

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setIsProcessing(false);
      setIsUploading(false);
      setParseError(null);
      setPreviewRows([]);
      setPayload(null);
      setMachineType('');
    }
  }, [isOpen]);

  const projectId = useMemo(() => {
    const value = project?.project?.projectId ?? project?.id ?? project?.projectId ?? 0;
    return parseNumber(String(value), 0);
  }, [project]);

  const projectName = useMemo(
    () => project?.project?.projectName ?? project?.projectName ?? project?.name ?? '',
    [project]
  );

  const isManualMode = machineType === MANUAL_MACHINE_SELECT_VALUE;

  useProductionModalAudit(
    isOpen,
    'Upload Weld Data',
    projectId > 0 ? projectId : undefined,
    projectName || null
  );

  const handleRemoveFile = () => {
    setFile(null);
    setParseError(null);
    setPreviewRows([]);
    setPayload(null);
  };

  const processTxtFile = async (selectedFile: File, type: WeldingSystemConfigEnum) => {
    setIsProcessing(true);
    setParseError(null);
    setPreviewRows([]);
    setPayload(null);

    try {
      const text = await selectedFile.text();
      const result = parseTxtToPayload(text, projectId, type);

      if (result.error) {
        setParseError(result.error);
        customNotification.error(result.error);
        return;
      }

      if (result.payload) {
        setPayload(result.payload);
        setPreviewRows(result.previewRows);
       // customNotification.success(`Parsed ${result.payload.records.length} weld records`);
      }
    } catch {
      setParseError('Failed to read or parse TXT file.');
      customNotification.error('Failed to read or parse TXT file.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (!isWeldingSystemConfig(machineType)) {
      // Should not happen since the FileUpload is disabled, but guard anyway.
      customNotification.error('Please select a welding machine before uploading the TXT file.');
      return;
    }
    const selectedFile = files[0];
    const isTxt = selectedFile.name.toLowerCase().endsWith('.txt') || selectedFile.type === 'text/plain';

    if (!isTxt) {
      customNotification.error('Please upload a TXT file');
      return;
    }

    setFile(selectedFile);
    void processTxtFile(selectedFile, machineType);
  };

  const handleMachineTypeChange = (value: string) => {
    if (!isMachineSelectValue(value) || value === machineType) return;
    setMachineType(value);
    // Clear standard TXT state when switching machines (Manual panel resets via isActive).
    if (file || payload || previewRows.length > 0 || parseError) {
      handleRemoveFile();
      if (value !== MANUAL_MACHINE_SELECT_VALUE) {
        customNotification.info('Machine type changed — please upload the TXT file again.');
      }
    }
  };

  const handleUpload = async () => {
    if (!payload) {
      customNotification.error('No payload generated. Upload a valid TXT file first.');
      return;
    }
    if (!projectId) {
      customNotification.error('Project ID is missing. Select a project and try again.');
      return;
    }

    setIsUploading(true);
    try {
      const timeput = payload.records.length * 3;
      const response = await weldAxiosConfig.post(WELD_BULK_UPLOAD_ENDPOINT, payload);
      if (response.status >= 200 && response.status < 300) {
        const weldNumbers = Array.from(
          new Set(
            payload.records
              .map((record) => String(record.Weld_number ?? record.weld_number ?? '').trim())
              .filter(Boolean)
          )
        );
        const jobNumbers = Array.from(
          new Set(
            payload.records
              .map((record) => String(record.Job_number ?? record.jobNumber ?? '').trim())
              .filter(Boolean)
          )
        );

        logWeldLogUploaded({
          projectId,
          projectName,
          fileName: file?.name ?? 'weld-log.txt',
          machineType: payload.machine_type,
          recordCount: payload.records.length,
          tRecordCount: payload.records.filter((record) => record.Record === 'T').length,
          sRecordCount: payload.records.filter((record) => record.Record === 'S').length,
          cRecordCount: payload.records.filter((record) => record.Record === 'C').length,
          weldNumbers,
          jobNumbers,
        });

        await setTimeout(() => {
          customNotification.success(`Uploaded ${payload.records.length} weld records successfully`);
          onUploadComplete?.();
          setIsUploading(false);
          onClose();
        }, timeput);
        return;
      }
      customNotification.error('Failed to upload weld data.');
    } catch (error: any) {
      customNotification.error('Failed to upload weld data.');
    } finally {
      //setIsUploading(false);
    }
  };

  const handleClose = () => {
    handleRemoveFile();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-[1200px] max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden bg-white">
        <div className="flex items-center gap-3 border-b border-[#E2E5EA] px-6 py-4 pr-14 flex-shrink-0">
          <div className="flex-shrink-0 w-10 h-10 bg-hero-red-10 rounded-full flex items-center justify-center">
            <Upload className="h-5 w-5 text-hero-red" />
          </div>
          <DialogTitle className="font-heading text-[18px] font-semibold text-blueprint-blue leading-tight">Upload Weld TXT Data</DialogTitle>
        </div>

        <div className="space-y-6 p-6 flex-1 overflow-y-auto overflow-x-hidden bg-white">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-hero-red text-white rounded-full flex items-center justify-center text-[12px] font-medium">
                1
              </div>
              <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Select Welding Machine</h3>
            </div>

            <div className="max-w-xs space-y-1.5">
              <Label htmlFor="welding-machine-type" className="text-[12px] font-medium text-text-grey">
                Welding system
              </Label>
              <Select
                value={machineType}
                onValueChange={handleMachineTypeChange}
                disabled={isProcessing || isUploading}
              >
                <SelectTrigger
                  id="welding-machine-type"
                  className="h-9 bg-white text-sm"
                  aria-label="Select welding machine type"
                >
                  <div className="flex items-center gap-2">
                    <Cpu className="h-4 w-4 text-heritage-grey" />
                    <SelectValue placeholder="Select machine" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {MACHINE_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex flex-col">
                        <span className="text-[13px] font-medium text-text-grey">{opt.label}</span>

                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {isManualMode ? (
            <ManualWeldFolderUploadPanel
              isActive={isOpen && isManualMode}
              project={project}
              onUploadComplete={() => {
                onUploadComplete?.();
                onClose();
              }}
              onCancel={handleClose}
            />
          ) : (
            <>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-hero-red text-white rounded-full flex items-center justify-center text-[12px] font-medium">
                    2
                  </div>
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Upload TXT File</h3>
                </div>

                <p className="text-[13px] text-text-grey">
                  Upload the weld machine TXT file. The file must have the standard weld machine format with metadata header rows followed by data rows.
                </p>

                {file ? (
                  <div className="flex items-center p-3 bg-page-bg border border-[#E2E5EA] rounded-lg">
                    <FileText className="h-4 w-4 text-heritage-grey mr-2 flex-shrink-0" />
                    <span className="text-[13px] text-text-grey flex-1 truncate">{file.name}</span>
                    {isProcessing && <span className="text-[12px] text-hero-red mr-2">Processing...</span>}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveFile}
                      className="h-6 w-6 p-0 hover:bg-hero-red-10 hover:text-readable-red"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <FileUpload
                    onFileSelect={handleFileSelect}
                    accept=".txt"
                    multiple={false}
                    buttonText="Upload Weld TXT File"
                    variant="outline"
                    size="lg"
                    className={`w-full ${!isWeldingSystemConfig(machineType) ? 'opacity-60' : ''}`}
                    disabled={!isWeldingSystemConfig(machineType)}
                  />
                )}

                {parseError && (
                  <div className="flex items-start gap-3 p-3 bg-[rgba(196,0,31,0.06)] border border-hero-red-20 rounded-lg text-[13px] text-readable-red">
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-readable-red" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">Invalid file format</p>
                      <p className="mt-0.5">{parseError}</p>
                      <p className="mt-2 text-[11px] uppercase tracking-wide text-readable-red/80">
                        Expected headers
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {REQUIRED_HEADER_GROUPS.map((g) => (
                          <span
                            key={g.label}
                            className="inline-flex items-center rounded border border-hero-red-20 bg-white px-1.5 py-0.5 text-[11px] text-readable-red"
                          >
                            {g.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {payload && !isProcessing && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-hero-red text-white rounded-full flex items-center justify-center text-[12px] font-medium">
                      <CheckCircle className="h-3.5 w-3.5" />
                    </div>
                    <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Preview</h3>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <span className="px-3 py-1 bg-page-bg border border-[#E2E5EA] rounded-full text-[12px] text-text-grey font-medium">
                      Total records: {payload.records.filter(record => record.Record === 'T').length}
                    </span>
                    <span className="px-3 py-1 bg-[rgba(0,39,63,0.06)] border border-[#E2E5EA] rounded-full text-[12px] text-blueprint-blue font-medium">
                      Project: {projectName || 'N/A'}
                    </span>

                    <span className="px-3 py-1 bg-hero-red-10 border border-hero-red-20 rounded-full text-[12px] text-readable-red font-medium">
                      Machine: {MACHINE_TYPE_OPTIONS.find((m) => m.value === payload.machine_type)?.label
                        || payload.machine_type}
                    </span>
                  </div>

                  <div className="overflow-x-auto rounded-lg border border-[#E2E5EA] text-[12px]">
                    <table className="min-w-full">
                      <thead className="bg-page-bg border-b border-[#E2E5EA]">
                        <tr>
                          {['Evt #', 'Job #', 'Weld #', 'Pass', 'Torch', 'Station', 'Travel', 'Volts', 'Current', 'Wire', 'Time'].map(
                            (h) => (
                              <th key={h} className="px-2 py-1.5 text-left whitespace-nowrap text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                                {h}
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#EEF0F3]">
                        {previewRows.map((row, i) => (
                          <tr key={`${row.eventNo}-${i}`} className="bg-white hover:bg-[#FAFBFC]">
                            <td className="px-2 py-1 whitespace-nowrap">{row.eventNo}</td>
                            <td className="px-2 py-1 whitespace-nowrap">{row.jobNumber}</td>
                            <td className="px-2 py-1 whitespace-nowrap">{row.weldNumber}</td>
                            <td className="px-2 py-1 whitespace-nowrap">{row.passName}</td>
                            <td className="px-2 py-1 whitespace-nowrap">{row.torch}</td>
                            <td className="px-2 py-1 text-center">{row.stationNumber}</td>
                            <td className="text-center">{row.travelSpeed}</td>
                            <td className="px-2 py-1 text-center">{row.volts}</td>
                            <td className="px-2 py-1 text-center">{row.current}</td>
                            <td className="px-2 py-1 text-center">{row.wireSpeed}</td>
                            <td className="px-2 py-1 text-center">{row.time}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {!isManualMode && (
          <DialogFooter className="gap-3 flex-shrink-0 border-t border-[#E2E5EA] bg-page-bg px-6 py-4">
            <Button variant="outline" onClick={handleClose} disabled={isUploading} className="min-w-[100px]">
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!payload || isProcessing || isUploading}
              loading={isUploading}
              className="bg-hero-red hover:bg-hero-red-60 text-white min-w-[120px] disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                {isUploading ? 'Uploading...' : 'Upload Data'}
              </div>
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
